interface PerformanceMetrics {
  pageLoadTime: number;
  componentMountTime: number;
  rerenderCount: number;
  timestamp: number;
}

interface PageMetrics {
  [pageKey: string]: PerformanceMetrics[];
}

class PerformanceMonitor {
  private metrics: PageMetrics = {};
  private pageStartTimes: Map<string, number> = new Map();
  private mountStartTimes: Map<string, number> = new Map();
  private rerenderCounts: Map<string, number> = new Map();
  private enabled: boolean = process.env.NODE_ENV === 'development';

  // 开始监控页面性能
  startPageLoad(pageKey: string) {
    if (!this.enabled) return;
    
    this.pageStartTimes.set(pageKey, performance.now());
    console.log(`🚀 开始加载页面: ${pageKey}`);
  }

  // 结束页面加载监控
  endPageLoad(pageKey: string) {
    if (!this.enabled) return;
    
    const startTime = this.pageStartTimes.get(pageKey);
    if (!startTime) return;

    const loadTime = performance.now() - startTime;
    this.recordMetric(pageKey, {
      pageLoadTime: loadTime,
      componentMountTime: 0,
      rerenderCount: this.rerenderCounts.get(pageKey) || 0,
      timestamp: Date.now()
    });

    console.log(`✅ 页面加载完成: ${pageKey}, 耗时: ${loadTime.toFixed(2)}ms`);
    this.pageStartTimes.delete(pageKey);
  }

  // 开始监控组件挂载
  startComponentMount(componentName: string) {
    if (!this.enabled) return;
    
    this.mountStartTimes.set(componentName, performance.now());
  }

  // 结束组件挂载监控
  endComponentMount(componentName: string) {
    if (!this.enabled) return;
    
    const startTime = this.mountStartTimes.get(componentName);
    if (!startTime) return;

    const mountTime = performance.now() - startTime;
    console.log(`🔧 组件挂载完成: ${componentName}, 耗时: ${mountTime.toFixed(2)}ms`);
    this.mountStartTimes.delete(componentName);

    return mountTime;
  }

  // 记录重新渲染
  recordRerender(pageKey: string) {
    if (!this.enabled) return;
    
    const currentCount = this.rerenderCounts.get(pageKey) || 0;
    this.rerenderCounts.set(pageKey, currentCount + 1);
    console.log(`🔄 页面重新渲染: ${pageKey}, 次数: ${currentCount + 1}`);
  }

  // 记录性能指标
  private recordMetric(pageKey: string, metric: PerformanceMetrics) {
    if (!this.metrics[pageKey]) {
      this.metrics[pageKey] = [];
    }
    
    this.metrics[pageKey].push(metric);
    
    // 只保留最近50条记录
    if (this.metrics[pageKey].length > 50) {
      this.metrics[pageKey] = this.metrics[pageKey].slice(-50);
    }
  }

  // 获取页面性能统计
  getPageStats(pageKey: string) {
    if (!this.enabled) return null;
    
    const pageMetrics = this.metrics[pageKey];
    if (!pageMetrics || pageMetrics.length === 0) return null;

    const loadTimes = pageMetrics.map(m => m.pageLoadTime).filter(t => t > 0);
    const avgLoadTime = loadTimes.reduce((sum, time) => sum + time, 0) / loadTimes.length;
    const minLoadTime = Math.min(...loadTimes);
    const maxLoadTime = Math.max(...loadTimes);
    
    return {
      pageKey,
      totalLoads: loadTimes.length,
      averageLoadTime: avgLoadTime,
      minLoadTime,
      maxLoadTime,
      lastLoadTime: loadTimes[loadTimes.length - 1] || 0,
      totalRerenders: this.rerenderCounts.get(pageKey) || 0
    };
  }

  // 获取所有页面的性能汇总
  getAllStats() {
    if (!this.enabled) return {};
    
    const stats: any = {};
    Object.keys(this.metrics).forEach(pageKey => {
      stats[pageKey] = this.getPageStats(pageKey);
    });
    
    return stats;
  }

  // 清除指定页面的性能数据
  clearPageStats(pageKey: string) {
    if (!this.enabled) return;
    
    delete this.metrics[pageKey];
    this.rerenderCounts.delete(pageKey);
    console.log(`🗑️ 清除页面性能数据: ${pageKey}`);
  }

  // 清除所有性能数据
  clearAllStats() {
    if (!this.enabled) return;
    
    this.metrics = {};
    this.rerenderCounts.clear();
    console.log('🗑️ 清除所有性能数据');
  }

  // 打印性能报告
  printPerformanceReport() {
    if (!this.enabled) return;
    
    console.group('📊 页面性能报告');
    
    const allStats = this.getAllStats();
    Object.values(allStats).forEach((stats: any) => {
      if (stats) {
        console.log(`📄 ${stats.pageKey}:`);
        console.log(`  平均加载时间: ${stats.averageLoadTime.toFixed(2)}ms`);
        console.log(`  最快加载时间: ${stats.minLoadTime.toFixed(2)}ms`);
        console.log(`  最慢加载时间: ${stats.maxLoadTime.toFixed(2)}ms`);
        console.log(`  总加载次数: ${stats.totalLoads}`);
        console.log(`  重新渲染次数: ${stats.totalRerenders}`);
      }
    });
    
    console.groupEnd();
  }

  // 启用/禁用监控
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    console.log(`🎛️ 性能监控${enabled ? '已启用' : '已禁用'}`);
  }

  // 检查是否启用
  isEnabled() {
    return this.enabled;
  }
}

// 创建全局性能监控实例
export const performanceMonitor = new PerformanceMonitor();

// 在开发环境下添加全局访问
if (process.env.NODE_ENV === 'development') {
  (window as any).performanceMonitor = performanceMonitor;
} 